import {
    Grid,
    IconButton,
    Popover,
    Slider,
    Typography,
    useTheme,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React from "react";
import hexToRGB from "utils/hexToRGB";
import { MontageSoundClipStore } from "../Stores/MontageSoundClipStore";

interface ClipAudioAdjustmentsProps {
    store: MontageSoundClipStore;
    clipId: string;
    margin: string;
}

interface AdjustmentRowProps {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    formatValue: (value: number) => string;
    onChange: (value: number) => void;
}

const AdjustmentRow = ({
    label,
    value,
    min,
    max,
    step,
    formatValue,
    onChange,
}: AdjustmentRowProps) => (
    <>
        <Grid item xs={12}>
            <Typography variant="caption">{label}</Typography>
        </Grid>
        <Grid item xs>
            <Slider
                aria-label={label}
                min={min}
                max={max}
                step={step}
                marks={[{ value: 1 }]}
                value={value}
                valueLabelDisplay="auto"
                valueLabelFormat={formatValue}
                onChange={(_event, newValue) => onChange(newValue as number)}
            />
        </Grid>
        <Grid item>
            <AcxButton
                color="primary"
                disabled={value === 1}
                onClick={() => onChange(1)}
            >
                Reset
            </AcxButton>
        </Grid>
    </>
);

// Per-clip volume and speed applied when the montage is merged
const ClipAudioAdjustments = observer(
    ({ store, clipId, margin }: ClipAudioAdjustmentsProps) => {
        const theme = useTheme();
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(
            null,
        );

        const volume = store.getClipVolume(clipId);
        const speed = store.getClipSpeed(clipId);
        const isAdjusted = volume !== 1 || speed !== 1;

        return (
            <>
                <IconButton
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{
                        margin,
                        color: isAdjusted
                            ? theme.palette.primary.main
                            : hexToRGB(theme.palette.primary.main, 0.5),
                    }}
                    title={`Volume: ${Math.round(
                        volume * 100,
                    )}%, Speed: ${speed}x`}
                    size="large"
                >
                    <TuneIcon />
                </IconButton>
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: "center", horizontal: "right" }}
                    transformOrigin={{ vertical: "center", horizontal: "left" }}
                >
                    <Grid
                        container
                        alignItems="center"
                        columnSpacing={2}
                        sx={{ width: 360, padding: theme.spacing(1, 2) }}
                    >
                        <AdjustmentRow
                            label="Volume"
                            value={volume}
                            min={0}
                            max={2}
                            step={0.05}
                            formatValue={(value) =>
                                `${Math.round(value * 100)}%`
                            }
                            onChange={(value) =>
                                store.setClipVolume(
                                    clipId,
                                    Math.round(value * 100) / 100,
                                )
                            }
                        />
                        <AdjustmentRow
                            label="Speed"
                            value={speed}
                            min={0.5}
                            max={2}
                            step={0.05}
                            formatValue={(value) => `${value}x`}
                            onChange={(value) =>
                                store.setClipSpeed(
                                    clipId,
                                    Math.round(value * 100) / 100,
                                )
                            }
                        />
                    </Grid>
                </Popover>
            </>
        );
    },
);

export default ClipAudioAdjustments;
